"use client";
import { FC } from "react";

import { format } from "date-fns";
import { sv, nb, enGB, da } from "date-fns/locale";
import { stegaClean } from "next-sanity";
import { MdEmail as Email, MdPhone as Phone } from "react-icons/md";

import Image from "next/image";
import Link from "next/link";

import { canonicalBaseLanguageURLs, localizedPaths } from "i18n/utils";

import { getAuthorImage } from "lib/server-utils";
import { countriesType, postAuthor, PostProps } from "lib/types";

import { Chip } from "components/chip";
import { Typography } from "components/typography";

export interface PostBylineProps {
  /**
   * An array of alternative post options.
   */
  alternatives?: PostProps["alternatives"];
  /**
   * An array of authors for the post.
   */
  authors?: Array<postAuthor>;
  /**
   * The contact person for the post.
   */
  contactPerson?: postAuthor;
  /**
   * The date of the post.
   */
  date: PostProps["date"];
  /**
   * An array of external authors for the post.
   */
  externalAuthors?: PostProps["externalAuthors"];
  locale: "en" | "sv" | "no" | "da";
  /**
   * The type of the post.
   */
  postType?: PostProps["type"];
  /**
   * The estimated reading time for the post.
   */
  readingTime?: PostProps["readingTime"];
  translations: {
    article: string;
    author: string;
    email: string;
    illustration: string;
    minutes: string;
    otherArticleVersions: string;
    phone: string;
    photo: string;
    published: string;
    readingTime: string;
    send: string;
  };
}
export const PostByline: FC<PostBylineProps> = ({
  alternatives,
  authors,
  contactPerson,
  date,
  externalAuthors,
  locale,
  postType,
  readingTime,
  translations,
}) => {
  const t = translations;
  const isArticle = postType === "article";
  const author = contactPerson || authors?.[0];
  const getLocale = () =>
    ({
      en: enGB,
      sv,
      no: nb,
      da,
    })[locale];
  const hasLangAlternative = alternatives && alternatives?.length > 0;
  const shouldShowMultipleAuthors = authors && authors.length > 0;

  const getLanguageName = (language: countriesType) =>
    ({
      en: "English",
      sv: "Svenska",
      no: "Norsk",
      da,
    })[language];
  const getReadTheArticleText = (language: countriesType) =>
    ({
      en: "Read the article",
      sv: "Läs artikeln",
      no: "Les artikkelen",
      da: "Læs artiklen",
    })[language];

  const multipleAuthorsByline = shouldShowMultipleAuthors && (
    <div className="not-prose relative mb-8 flex flex-row flex-wrap items-center gap-5 pt-3">
      {authors.map((a, index) =>
        a?.photoUrl ? (
          <Image
            alt={`${a.title}, ${t.photo}`}
            className={`h-auto rounded-full ${index > 0 ? "z-[-1] ml-[-30px]" : ""}`}
            height={150}
            key={a.title}
            loading="eager"
            src={a.photoUrl}
            width={141}
          />
        ) : null,
      )}
      {externalAuthors?.map((a) =>
        a?.illustrationUrl ? (
          <Image
            alt=""
            className="z-[-1] ml-[-10px] h-auto rounded-full"
            height={150}
            key={a.name}
            loading="eager"
            src={a.illustrationUrl}
            width={141}
          />
        ) : null,
      )}
      <div className="space-y-2">
        <div>
          {authors.map((a, index) => (
            <span className="text-lg font-semibold lg:text-xl" key={a.title}>
              {index > 0 && ` & `}
              {a.title}
              {externalAuthors && " (Useit)"}
            </span>
          ))}
          {externalAuthors?.map((a) => (
            <span className="text-lg font-semibold lg:text-xl" key={a.name}>
              {` & `}
              {a.name}
            </span>
          ))}
        </div>

        {date && (
          <Typography colorVariant="secondary" responsive={false}>
            <time dateTime={date}>
              {`${t.published} ${format(new Date(date), "d MMMM yyyy", {
                locale: getLocale(),
              })}`}
            </time>
          </Typography>
        )}
        {readingTime && (
          <Typography
            className="mt-1"
            colorVariant="secondary"
            responsive={false}
          >
            {`${t.readingTime} ${readingTime.estimatedReadingTime} ${t.minutes}`}
          </Typography>
        )}
        {isArticle && <Chip color="green" label={t.article} />}
      </div>
    </div>
  );

  const singleAuthorByline = (
    <div className="not-prose flex flex-wrap gap-y-4 pb-3 lg:flex-row">
      <div className="relative mr-4">
        {author && getAuthorImage(author, t.photo).image !== "" ? (
          <Image
            alt={getAuthorImage(author, t.photo).alt}
            className="order-1 rounded-full"
            height={200}
            loading="eager"
            src={getAuthorImage(author, t.photo).image}
            width={200}
          />
        ) : (
          <div className="order-1 w-[200px] rounded-full bg-blue" />
        )}
      </div>
      <div className="min-w-[50%] flex-1 space-y-2 lg:ml-4">
        <Typography className="order-2 mb-2" component="h2" variant="h3">
          <span className="visually-hidden">{`${t.author}: `} </span>
          {author?.title}
        </Typography>
        {author?.role && (
          <Typography className="mb-3">{author.role[locale]}</Typography>
        )}
        {author?.email && (
          <Link
            className="text-link order-3 mb-4 mt-3 block hyphens-auto"
            href={`mailto:${author.email}`}
          >
            <p>
              <Email className="mr-2 inline-flex text-2xl" />
              <span>{author.email}</span>
            </p>
          </Link>
        )}
        {author?.phone && (
          <Link
            className="text-link order-4 my-3 block hyphens-auto"
            href={`tel:${author.phone.replace(/\s/g, "")}`}
          >
            <p>
              <Phone className="mr-2 inline-flex text-2xl" />
              <span>{author.phone}</span>
            </p>
          </Link>
        )}
        {date && isArticle && (
          <Typography colorVariant="secondary" responsive={false}>
            <time dateTime={date}>
              {`${t.published} ${format(new Date(date), "d MMMM yyyy", {
                locale: getLocale(),
              })}`}
            </time>
          </Typography>
        )}
        {readingTime && (
          <Typography colorVariant="secondary" responsive={false}>
            {`${t.readingTime} ${readingTime.estimatedReadingTime} ${t.minutes}`}
          </Typography>
        )}
        {hasLangAlternative && (
          <div
            aria-label={t.otherArticleVersions}
            className="w-fit space-y-2"
            role="group"
          >
            {alternatives?.map((post) => {
              if (post === null) return null;
              return (
                <Typography key={post.slug} responsive={false}>
                  {post && post?.language && (
                    <a
                      href={`${canonicalBaseLanguageURLs(post.language)}${
                        localizedPaths(post.language).posts
                      }/${stegaClean(post.slug)}/`}
                      lang={post.language}
                    >
                      {`${getReadTheArticleText(
                        post.language,
                      )} ${getLanguageName(post.language)}`}
                    </a>
                  )}
                </Typography>
              );
            })}
          </div>
        )}
        {isArticle && <Chip color="green" label={t.article} />}
      </div>
    </div>
  );

  const getAuthorsByline = () => {
    if (contactPerson) return singleAuthorByline;
    if (
      authors &&
      (authors.length > 1 || (externalAuthors && externalAuthors?.length > 0))
    )
      return multipleAuthorsByline;
    return singleAuthorByline;
  };

  return getAuthorsByline();
};
export default PostByline;
